import { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import {
  selectCatalogQuery,
  selectCategory,
  selectRenderableOnly,
  selectSearchText,
} from '../../store/selectors/index.js';
import { useSearchCatalogQuery } from '../../services/catalogApi.js';
import CatalogFilters from './CatalogFilters.js';
import CategoryTree from './CategoryTree.js';
import ResultList from './ResultList.js';

/**
 * Browse the catalog by category.
 *
 * The whole catalog is fetched once and filtered client-side. It is 188 records,
 * so filtering in the browser is instant, and the alternative - a request per
 * keystroke against a CSW that takes eleven seconds cold - would be both slower
 * and rude to a server we do not operate.
 */
export default function CatalogPanel() {
  const dispatch = useAppDispatch();
  const query = useAppSelector(selectCatalogQuery);
  const category = useAppSelector(selectCategory);
  const searchText = useAppSelector(selectSearchText);
  const renderableOnly = useAppSelector(selectRenderableOnly);

  const { data, isLoading, isError, error, refetch } = useSearchCatalogQuery(query);

  const heading = useMemo(() => {
    if (!data) return '';
    const n = data.total;
    const noun = n === 1 ? 'dataset' : 'datasets';
    if (category) return `${n} ${noun} in ${category}`;
    if (searchText) return `${n} ${noun} matching "${searchText}"`;
    return `${n} ${noun}`;
  }, [data, category, searchText]);

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={
            <Typography
              component="button"
              variant="caption"
              onClick={() => void refetch()}
              sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Retry
            </Typography>
          }
        >
          The dataset catalog could not be loaded.
          {typeof error === 'object' && error && 'status' in error ? ` (${String(error.status)})` : ''}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CatalogFilters
        categories={data?.categories ?? []}
        renderableOnly={renderableOnly}
        dispatch={dispatch}
      />

      {isLoading ? (
        <Box sx={{ p: 2 }} aria-busy="true" aria-label="Loading datasets">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={38} sx={{ mb: 0.5 }} />
          ))}
        </Box>
      ) : (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            aria-live="polite"
            sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}
          >
            {heading}
          </Typography>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {category || searchText ? (
              <ResultList records={data?.records ?? []} />
            ) : (
              <CategoryTree categories={data?.categories ?? []} records={data?.records ?? []} />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
